import dynamic from 'next/dynamic';
import settings from '@/settings';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import { getShortenCountryName } from '@/lib/use-origin-country';

const UsPrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/US'),
);
const UkPrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/UK'),
);
const CaPrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/CA'),
);
const AuPrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/AU'),
);
const NzPrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/NZ'),
);
const SgPrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/SG'),
);
const FrPrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/FR'),
);
const DePrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/DE'),
);
const EsPrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/ES'),
);
const ItPrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/IT'),
);
const SePrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/SE'),
);
const AtPrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/AT'),
);
const IePrivacyPolicy = dynamic(
  () => import('@/components/policies/privacy-policy/IE'),
);

const renderPolicy = () => {
  switch (settings.store) {
    case 'reebelo-dev':
    case 'reebelo-us':
      return <UsPrivacyPolicy />;
    case 'reebelo-uk':
      return <UkPrivacyPolicy />;
    case 'reebelo-ca':
      return <CaPrivacyPolicy />;
    case 'reebelo-au':
      return <AuPrivacyPolicy />;
    case 'reebelo-nz':
      return <NzPrivacyPolicy />;
    case 'quista':
      return <SgPrivacyPolicy />;
    case 'reebelo-fr':
      return <FrPrivacyPolicy />;
    case 'reebelo-de':
      return <DePrivacyPolicy />;
    case 'reebelo-es':
      return <EsPrivacyPolicy />;
    case 'reebelo-it':
      return <ItPrivacyPolicy />;
    case 'reebelo-se':
      return <SePrivacyPolicy />;
    case 'reebelo-ie':
      return <IePrivacyPolicy />;
    case 'reebelo-at':
      return <AtPrivacyPolicy />;
    default:
      return null;
  }
};

const PrivacyPolicy = () => {
  const content = renderPolicy();

  if (!content) {
    return null;
  }

  const country = getShortenCountryName();
  const localizedMeta: Partial<
    Record<typeof settings.store, { title: string; description: string }>
  > = {
    'reebelo-fr': {
      title: `Politique de confidentialité - Reebelo ${country}`,
      description: `Découvrez comment Reebelo ${settings.country} collecte, utilise et protège vos données à caractère personnel. Consultez ici la Politique de confidentialité de Reebelo ${settings.country}.`,
    },
    'reebelo-de': {
      title: `Datenschutzerklärung - Reebelo ${country}`,
      description: `Erfahren Sie, wie Reebelo ${settings.country} Ihre personenbezogenen Daten erhebt, verwendet und schützt. Hier finden Sie die Datenschutzerklärung von Reebelo ${settings.country}.`,
    },
    'reebelo-at': {
      title: `Datenschutzerklärung - Reebelo ${country}`,
      description: `Erfahren Sie, wie Reebelo ${settings.country} Ihre personenbezogenen Daten erhebt, verwendet und schützt. Hier finden Sie die Datenschutzerklärung von Reebelo ${settings.country}.`,
    },
    'reebelo-es': {
      title: `Política de privacidad - Reebelo ${country}`,
      description: `Descubre cómo Reebelo ${settings.country} recopila, utiliza y protege tus datos personales. Consulta aquí la Política de privacidad de Reebelo ${settings.country}.`,
    },
    'reebelo-it': {
      title: `Informativa sulla privacy - Reebelo ${country}`,
      description: `Scopri come Reebelo ${settings.country} raccoglie, utilizza e protegge i tuoi dati personali. Consulta qui l’Informativa sulla privacy di Reebelo ${settings.country}.`,
    },
    'reebelo-se': {
      title: `Integritetspolicy - Reebelo ${country}`,
      description: `Läs hur Reebelo ${country} samlar in, använder och skyddar dina personuppgifter. Här hittar du integritetspolicyn för Reebelo ${country}.`,
    },
  };
  const meta = localizedMeta[settings.store] ?? {
    title: `Privacy Policy - Reebelo ${country}`,
    description: `Reebelo ${settings.country} is APAC’s leading marketplace for sustainable tech with offices in ${settings.country_office_locations}. The positive customer experience at Reebelo ${settings.country} is of utmost importance to us. Please check here for Reebelo ${settings.country} Privacy Policy.`,
  };

  return (
    <>
      <WebsiteSchema title={meta.title} description={meta.description} />
      {content}
    </>
  );
};

export default PrivacyPolicy;
